/**
 * Draggable top-nav ordering + hide/unhide — user preference, persisted to backend.
 *
 * Anonymous users see the default order (no items hidden). Logged-in users see
 * their saved order and visibility (falling back to default for any newly-added
 * nav item not yet in their list).
 */
import { useEffect, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { KeyboardSensor } from "@dnd-kit/core";
import { GripVertical, Check, X, Settings2, Eye, EyeOff, Plus } from "lucide-react";
import { userPrefs as prefsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import { log } from "@/lib/log";
// Default order — source of truth for the inline TOP-LEVEL navbar items.
// Secondary items (Library, Glossary, Newsletters, Guide, Webinars, Speakers,
// Shorts, AI Eval, Tools, Carousel Lite, Finder, Help) live inside the
// grouped dropdowns defined in `navGroups.jsx` and are NOT reorderable
// per-item (they're sub-items of their group, not first-class top-nav).
//
// If you add a new flagship product, add it here AND remove it from any
// dropdown group so it doesn't appear twice.
export const DEFAULT_NAV_ITEMS = [
  "india", "riskpulse", "moneyflow", "automations", "pro",
];

// Human-readable labels for the hidden-item drawer (matches Navbar labels).
const NAV_LABELS = {
  india: "India",
  riskpulse: "India Risk Pulse",
  moneyflow: "Money Flow",
  automations: "Automations",
  pro: "Pro",
};

function mergeOrder(saved, defaults) {
  if (!Array.isArray(saved) || saved.length === 0) return defaults;
  const known = new Set(defaults);
  const result = saved.filter((id) => known.has(id));
  for (const d of defaults) {
    if (!result.includes(d)) result.push(d);
  }
  return result;
}

function sanitizeHidden(saved, defaults) {
  if (!Array.isArray(saved)) return [];
  const known = new Set(defaults);
  return saved.filter((id) => known.has(id));
}

export function useNavOrder() {
  const { user } = useAuth();
  const [order, setOrder] = useState(DEFAULT_NAV_ITEMS);
  const [hidden, setHidden] = useState([]);
  const [loaded, setLoaded] = useState(!user);

  useEffect(() => {
    if (!user) {
      setOrder(DEFAULT_NAV_ITEMS);
      setHidden([]);
      setLoaded(true);
      return;
    }
    prefsApi.get()
      .then((p) => {
        setOrder(mergeOrder(p?.nav_order, DEFAULT_NAV_ITEMS));
        setHidden(sanitizeHidden(p?.hidden_nav, DEFAULT_NAV_ITEMS));
      })
      .catch(() => {
        setOrder(DEFAULT_NAV_ITEMS);
        setHidden([]);
      })
      .finally(() => setLoaded(true));
  }, [user]);

  const saveOrder = async (next) => {
    setOrder(next);
    try {
      await prefsApi.update({ nav_order: next });
    } catch (err) {
      toast.error("Couldn't save nav order — try again");
      log.error("[nav-order] save failed:", err);
    }
  };

  const saveHidden = async (nextHidden) => {
    setHidden(nextHidden);
    try {
      await prefsApi.update({ hidden_nav: nextHidden });
    } catch (err) {
      toast.error("Couldn't save menu visibility — try again");
      log.error("[nav-hidden] save failed:", err);
    }
  };

  const hideItem = (id) => {
    if (hidden.includes(id)) return;
    saveHidden([...hidden, id]);
  };

  const showItem = (id) => {
    if (!hidden.includes(id)) return;
    saveHidden(hidden.filter((h) => h !== id));
  };

  const resetOrder = async () => {
    setOrder(DEFAULT_NAV_ITEMS);
    setHidden([]);
    try {
      await prefsApi.update({ nav_order: [], hidden_nav: [] });
      toast.success("Top menu reset to default");
    } catch (err) {
      log.error("[nav-order] reset failed:", err);
    }
  };

  return { order, hidden, saveOrder, saveHidden, hideItem, showItem, resetOrder, loaded };
}

function SortableItem({ id, children, onHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="inline-flex items-center gap-1 rounded-md border border-dashed border-emerald-400/40 bg-emerald-500/5 px-1.5 py-0.5 select-none"
      data-testid={`nav-sortable-${id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-emerald-300 hover:text-emerald-200"
        title="Drag to reorder"
        aria-label={`Drag ${NAV_LABELS[id] || id}`}
        data-testid={`nav-sortable-handle-${id}`}
      >
        <GripVertical className="h-3 w-3" />
      </button>
      {children}
      {onHide && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onHide(id); }}
          className="text-emerald-300/70 hover:text-rose-400 ml-0.5"
          title={`Hide ${NAV_LABELS[id] || id}`}
          aria-label={`Hide ${NAV_LABELS[id] || id}`}
          data-testid={`nav-hide-${id}`}
        >
          <EyeOff className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function SortableNav({ items, hidden = [], renderItem, editing, onReorder, onHide }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const hiddenSet = new Set(hidden);
  // visible == items - hidden (preserving order).
  const visible = items.filter((id) => !hiddenSet.has(id));
  if (!editing) {
    return (
      <>
        {visible.map((id) => (
          <span key={id}>{renderItem(id)}</span>
        ))}
      </>
    );
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(e) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = items.indexOf(active.id);
        const newIdx = items.indexOf(over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        onReorder(arrayMove(items, oldIdx, newIdx));
      }}
    >
      <SortableContext items={visible} strategy={horizontalListSortingStrategy}>
        {visible.map((id) => (
          <SortableItem key={id} id={id} onHide={onHide}>
            {renderItem(id)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

/**
 * Drawer of hidden nav items, shown when in editing mode and user has
 * hidden one or more items. Clicking + restores the item to its prior
 * position in `items`.
 */
export function HiddenNavDrawer({ hidden, onShow }) {
  if (!hidden || hidden.length === 0) return null;
  return (
    <div
      className="w-full mt-1 inline-flex flex-wrap items-center gap-1.5"
      data-testid="nav-hidden-drawer"
    >
      <span className="text-[10px] uppercase tracking-wide text-rp-textFaint">Hidden:</span>
      {hidden.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onShow(id)}
          className="inline-flex items-center gap-1 rounded-md border border-rp-border bg-rp-surface2 px-1.5 py-0.5 text-[11px] text-rp-textDim hover:text-emerald-300 hover:border-emerald-400/40"
          title={`Show ${NAV_LABELS[id] || id}`}
          data-testid={`nav-show-${id}`}
        >
          <Plus className="h-3 w-3" /> {NAV_LABELS[id] || id}
        </button>
      ))}
    </div>
  );
}

export function NavCustomiseToggle({ editing, onToggle, onReset, hasOrder }) {
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => onToggle(true)}
        className="text-xs text-rp-textFaint hover:text-emerald-300 inline-flex items-center gap-1"
        title="Drag to reorder · Hide items you don't use"
        data-testid="nav-customise-toggle"
      >
        <Settings2 className="h-3 w-3" /> Customise
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => onToggle(false)}
        className="text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-500/10"
        data-testid="nav-customise-done"
      >
        <Check className="h-3 w-3" /> Done
      </button>
      {hasOrder && (
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-rp-textDim hover:text-rp-text inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-rp-border"
          title="Reset to default order + show all items"
          data-testid="nav-customise-reset"
        >
          <X className="h-3 w-3" /> Reset
        </button>
      )}
      <span className="inline-flex items-center gap-1 text-[10px] text-rp-textFaint" data-testid="nav-customise-hint">
        <Eye className="h-3 w-3" /> drag + hide
      </span>
    </div>
  );
}
